import {
  PunchupTicket,
  TicketColors,
} from "@punchup/ui-common/components/common";
import {
  ShowWithComedian,
  UNKNOWN_POSTAL_CODE,
  VideoComedianContext,
  getIpData,
  getUserZipcodeOrFallbackToIp,
} from "@punchup/data/legacy";
import { UserEvents, VideoEvents, trackEvent } from "@punchup/analytics";
import { getCookie, setCookie } from "cookies-next";
import { useEffect, useState } from "react";

import { Database } from "@punchup/data/supabase.types";
import { VideoState } from "@punchup/video/types";
import { X } from "lucide-react";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import dayjs from "@punchup/utils/dayjs";
import { getApiClient } from "@punchup/utils/apiRegistry";
import { logException } from "@punchup/sentry";
import { shuffleArray } from "@punchup/utils/shuffleArray";
import styles from "./VideoTicketLinkOverlay.module.css";

const shuffledColors = shuffleArray(TicketColors);

async function fetchShows(path: string): Promise<ShowWithComedian[]> {
  const data = await getApiClient().get<unknown>(path);
  if (!Array.isArray(data)) {
    throw new Error(
      `[videoTicketLinkOverlay] /api/shows did not return an array (got ${typeof data})`
    );
  }
  return data as ShowWithComedian[];
}

export default function VideoTicketLinkOverlay({
  context,
  videoState,
}: {
  context: VideoComedianContext;
  videoState: VideoState;
}) {
  const [nearbyShows, setNearbyShows] = useState<ShowWithComedian[] | null>();
  const [isMouseHover, setIsMouseHover] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [timeoutId, setTimeoutId] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  useEffect(() => {
    const supabase = createClientComponentClient<Database>();
    getUserZipcodeOrFallbackToIp(supabase).then(async (data) => {
      if (!data || !data.zipcode || !data.country_code) {
        // No zipcode, can't show nearby shows
        trackEvent(VideoEvents.NO_ZIPCODE_AVAILABLE_FOR_OVERLAY, {
          ComedianId: context.comedian.id,
          ComedianName: context.comedian.display_name,
        });
        return;
      }

      const { zipcode, country_code } = data;

      let shows: ShowWithComedian[] = [];
      try {
        // Get shows based on user location within 5mi
        shows = await fetchShows(
          `/api/shows?postalCode=${zipcode}&countryCode=${country_code}&radius=${5}&comedianId=${context.comedian.id}&startDatetime=${dayjs().utc().toISOString()}`
        );

        if (shows.length === 0) {
          // No shows within 5mi, try again with bigger radius
          shows = await fetchShows(
            `/api/shows?postalCode=${zipcode}&countryCode=${country_code}&radius=${30}&comedianId=${context.comedian.id}&startDatetime=${dayjs().utc().toISOString()}`
          );
        }

        if (shows.length === 0) {
          // Still no shows using the stored location, try again with the user's current location (from IP)
          const { postalCode: ipZipcode, countryCode: ipCountryCode } =
            await getIpData();

          if (
            ipCountryCode &&
            ipZipcode &&
            ipZipcode !== UNKNOWN_POSTAL_CODE &&
            ipZipcode != zipcode
          ) {
            shows = await fetchShows(
              `/api/shows?postalCode=${ipZipcode}&countryCode=${ipCountryCode}&radius=${30}&comedianId=${context.comedian.id}&startDatetime=${dayjs().utc().toISOString()}`
            );
          }
        }
      } catch (e) {
        logException(e, {
          data: "[videoTicketLinkOverlay] Error fetching shows",
        });
        shows = [];
      }

      if (shows.length === 0) {
        // Giving up
        trackEvent(VideoEvents.NO_TICKETS_AVAILABLE_FOR_OVERLAY, {
          ComedianId: context.comedian.id,
          ComedianName: context.comedian.display_name,
          Zipcode: zipcode,
        });
        return;
      } else {
        trackEvent(VideoEvents.SHOWED_TICKETS_OVERLAY, {
          ComedianId: context.comedian.id,
          ComedianName: context.comedian.display_name,
          Zipcode: zipcode,
          Count: shows.length,
        });
      }

      setNearbyShows(shows);

      // Check if the user has dismissed this comedian's tickets overlays
      const isDisabled =
        getCookie(`dismissedComedianTickets-${context.comedian.id}`) != null;
      setIsEnabled(!isDisabled);
    });
  }, [context.comedian]);

  const dismiss = () => {
    setIsEnabled(false);

    trackEvent(VideoEvents.CLICKED_DISMISS_TICKETS, {
      ComedianId: context.comedian.id,
      ComedianName: context.comedian.display_name,
    });

    // Don't show tickets for this comedian for the next 7 days
    setCookie(`dismissedComedianTickets-${context.comedian.id}`, 1, {
      maxAge: 60 * 60 * 24 * 7, // one week in seconds
    });
  };

  if (!Array.isArray(nearbyShows) || nearbyShows.length === 0 || !isEnabled) {
    return null;
  }

  const showFull =
    isMouseHover ||
    (videoState.currentTime > 10 && videoState.currentTime < 20) ||
    (!videoState.isPlaying && videoState.currentTime > 0);

  return (
    <div className={styles.overlayContainer}>
      <div
        onMouseEnter={() => setIsMouseHover(true)}
        onTouchEnd={() => {
          setIsMouseHover(true);
          setTimeoutId(setTimeout(() => setIsMouseHover(false), 5000));
        }}
        className={`vjs-overlay ${styles.minimized} ${
          showFull ? "hidden" : ""
        }`}
      >
        <div className={styles.comingText}>
          {context.comedian.display_name_short} is coming to{" "}
          {nearbyShows[0]?.location || "your area"}!
        </div>
        <button className={styles.dismissButton} onClick={() => dismiss()}>
          <X />
        </button>
      </div>

      <div
        onMouseEnter={() => setIsMouseHover(true)}
        onMouseLeave={() => setIsMouseHover(false)}
        className={`vjs-overlay ${styles.overlay} ${
          showFull ? styles.overlayShow : ""
        }`}
      >
        <div className="flex flex-row">
          <div className={styles.comingText}>
            {context.comedian.display_name_short} is coming to{" "}
            {nearbyShows[0]?.location || "your area"}!
          </div>
          <button className={styles.dismissButton} onClick={() => dismiss()}>
            <X />
          </button>
        </div>

        <div
          className={styles.ticketsContainer}
          onScroll={() => {
            // For mobile, there is no mouseOut event, so we use touches instead, and we auto-dismiss
            // after 5s of no interaction. If they scroll the ticket container, this resets the timer.
            // It's also fine if this happens on desktop, it just means the tickets will disappear after 5s.
            if (timeoutId) clearTimeout(timeoutId);
            setTimeoutId(setTimeout(() => setIsMouseHover(false), 5000));
          }}
        >
          {nearbyShows?.map((show, idx) => {
            return (
              <PunchupTicket
                key={show.id}
                className="scale-50 md:scale-100"
                datetime={dayjs.utc(show.datetime).format("YYYY-MM-DD HH:mm")}
                color={shuffledColors[idx]}
                onClick={() => {
                  trackEvent(
                    UserEvents.BUY_TICKETS_CLICKED,
                    {
                      ComedianId: context.comedian.id,
                      ComedianName: context.comedian.display_name,
                      ShowId: show.id,
                      ShowVenue: show.venue,
                      ShowDate: dayjs.utc(show.datetime).toISOString(),
                      Source: "video-overlay",
                      IsNearby: true,
                    },
                    { send_immediately: true }
                  );

                  window.open(show.ticket_link);
                }}
              />
            );
          })}

          <button
            className="flex items-center w-full gap-2 transition-all duration-100 ease-in-out shadow-sm btn justify-stretch primary"
            onClick={() => {
              trackEvent(VideoEvents.CLICKED_ALL_TICKETS, {
                ComedianId: context.comedian.id,
                ComedianName: context.comedian.display_name,
              });

              const ticketsSection = document.getElementById("tickets");
              if (ticketsSection) {
                ticketsSection.scrollIntoView({ behavior: "smooth" });
              } else {
                window.open(`/${context.comedian.slug}/tickets`);
              }
            }}
          >
            View All Tickets
          </button>
        </div>
      </div>
    </div>
  );
}
